<template>
    <div class="order-filter-manager" v-if="gridApi">
        <div class="row">
            <label class="label">Saved filters</label>
            <select class="select" v-model="selectedId" @change="onSelectChange">
                <option :value="null">None</option>
                <option v-for="f in filters" :key="f.userSavedGridSearchId" :value="f.userSavedGridSearchId">
                    {{ f.shouldApplyByDefault ? '&#x2B50; ' : '' }}{{ f.name }}
                </option>
            </select>

            <button class="btn" :disabled="!hasGridState" @click="saveAsNew">Save as new</button>
            <button class="btn" :disabled="!currentFilter || !hasGridState" @click="saveCurrent">Save</button>
            <button class="btn" :disabled="!currentFilter" @click="startRename">Rename</button>
            <button class="btn danger" :disabled="!currentFilter" @click="deleteCurrent">Delete</button>

            <button class="btn" :disabled="!currentFilter" v-if="currentFilter" @click="toggleDefault">
                {{ currentFilter.shouldApplyByDefault ? 'Unset default' : 'Set as default' }}
            </button>
        </div>

        <div v-if="showNameInput" class="name-row">
            <input class="name-input" v-model="tempName" placeholder="Filter name" @keyup.enter="confirmName" />
            <button class="btn primary" @click="confirmName">OK</button>
            <button class="btn" @click="cancelName">Cancel</button>
        </div>
    </div>
</template>

<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';

export default {
    name: 'GridFilterManager',
    mixins: [GeneralMixin],
    props: {
        gridApi: { type: Object, required: true },
        getSearchText: { type: Function, default: null },
        setSearchText: { type: Function, default: null },
        gridUrl: { type: String, default: '' }
    },
    data() {
        return {
            filters: [],
            selectedId: null,
            showNameInput: false,
            tempName: '',
            pendingAction: null, // 'create' | 'rename'
            hasAutoAppliedDefault: false
        };
    },
    computed: {
        currentFilter() {
            return this.filters.find((f) => f.userSavedGridSearchId === this.selectedId) || null;
        },
        hasGridState() {
            const { filterModel, columnState, sortModel, searchText } = this.collectCurrentState();
            const hasFilter = filterModel && Object.keys(filterModel).length > 0;
            const hasColumns = Array.isArray(columnState) && columnState.length > 0;
            const hasSort = Array.isArray(sortModel) && sortModel.length > 0;
            const hasSearch = !!(searchText && String(searchText).trim());
            return hasFilter || hasColumns || hasSort || hasSearch;
        }
    },
    async mounted() {
        await this.setupDefaultFilter();
    },
    methods: {
        triggerServerSideRefresh() {
            this.gridApi.refreshServerSide({ purge: true });
        },

        async loadDefaultSavedFilter() {
            const api = `${this.gridUrl}`;

            try {
                const request = await handleRequests(api);

                const transformedData = request.data.map((item) => {
                    const parsedModel = JSON.parse(item.searchRequestJson);
                    return {
                        name: item.name,
                        shouldApplyByDefault: item.shouldApplyByDefault,
                        model: parsedModel,
                        userSavedGridSearchId: item.userSavedGridSearchId
                    };
                });

                const defaultSearch = transformedData.find((x) => x.shouldApplyByDefault);
                this.filters = transformedData;

                this.selectedId = defaultSearch ? defaultSearch.userSavedGridSearchId : null;

                return defaultSearch;
            } catch (e) {
                const message = 'There was an error loading the filter';
                showErrorMessage(this, message, e);
                return null;
            }
        },

        async setupDefaultFilter() {
            // Pre-apply default filter before datasource is set to avoid an initial unfiltered load
            const def = await this.loadDefaultSavedFilter();
            if (def && def.model) {
                this.setupGridModel(def.model);
                this.defaultFilterId = def.userSavedGridSearchId;
            }

            this.$emit('default-filter-loaded');
        },

        async saveToStorage() {
            const api = `${this.gridUrl}`;
            const payload = {
                method: 'post',
                data: {
                    name: this.currentFilter.name,
                    searchRequestJson: JSON.stringify(this.currentFilter.model),
                    shouldApplyByDefault: this.currentFilter.shouldApplyByDefault
                }
            };

            try {
                const response = await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully saved the filter.',
                    type: 'success'
                });
                this.currentFilter.userSavedGridSearchId = response.data;
                this.selectedId = response.data;
            } catch (e) {
                const message = 'There was an error saving the filter';
                showErrorMessage(this, message, e);
            }
        },

        async updateFilter() {
            const api = `${this.gridUrl}/${this.currentFilter.userSavedGridSearchId}`;
            const payload = {
                method: 'put',
                data: {
                    name: this.currentFilter.name,
                    searchRequestJson: JSON.stringify(this.currentFilter.model),
                    shouldApplyByDefault: this.currentFilter.shouldApplyByDefault
                }
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully saved the filter.',
                    type: 'success'
                });
            } catch (e) {
                const message = 'There was an error saving the filter';
                showErrorMessage(this, message, e);
            }
        },
        collectCurrentState() {
            const filterModel = this.gridApi.getFilterModel ? this.gridApi.getFilterModel() : {};
            const sortModel = this.gridApi.getSortModel ? this.gridApi.getSortModel() : [];
            const columnState = this.gridApi.getColumnState ? this.gridApi.getColumnState() : [];
            const searchText = this.getSearchText ? this.getSearchText() : null;
            return { filterModel, sortModel, columnState, searchText };
        },
        applyState(model) {
            if (!model) {
                if (this.gridApi.setFilterModel) {
                    this.gridApi.setFilterModel(null);
                }
                if (this.gridApi.setSortModel) {
                    this.gridApi.setSortModel(null);
                }
                if (this.setSearchText) {
                    this.setSearchText('');
                }
                if (this.gridApi.resetColumnState) {
                    this.gridApi.resetColumnState();
                }

                setTimeout(() => this.triggerServerSideRefresh(), 0);
                return;
            }
            this.setupGridModel(model);
            setTimeout(() => this.triggerServerSideRefresh(), 0);
        },

        setupGridModel(model) {
            const { filterModel, sortModel, columnState, searchText } = model;
            if (this.gridApi.setFilterModel && filterModel) 
                this.gridApi.setFilterModel(filterModel);
            if (this.gridApi.applyColumnState && Array.isArray(columnState)) {
                this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
            }
            if (this.gridApi.setSortModel && Array.isArray(sortModel)) 
                this.gridApi.setSortModel(sortModel);
            if (this.setSearchText && typeof searchText !== 'undefined') 
                this.setSearchText(searchText);
        },

        onSelectChange() {
            if (this.currentFilter) 
                this.applyState(this.currentFilter.model);
            else 
                this.applyState(null);
        },
        saveAsNew() {
            this.pendingAction = 'create';
            this.tempName = '';
            this.showNameInput = true;
        },
        saveCurrent() {
            if (!this.currentFilter) 
                return;
            const model = this.collectCurrentState();
            this.currentFilter.model = model;
            this.updateFilter();
        },
        startRename() {
            if (!this.currentFilter) 
                return;
            this.pendingAction = 'rename';
            this.tempName = this.currentFilter.name;
            this.showNameInput = true;
        },
        async deleteCurrent() {
            if (!this.currentFilter) 
                return;

            const oldSelectedFilter = this.currentFilter.userSavedGridSearchId;
            const api = `${this.gridUrl}/${oldSelectedFilter}`;
            const payload = {
                method: 'delete'
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully deleted the filter.',
                    type: 'success'
                });

                this.selectedId = null;

                const index = this.filters.findIndex((f) => f.userSavedGridSearchId === oldSelectedFilter);
                if (index !== -1) {
                    this.filters.splice(index, 1);
                }
            } catch (e) {
                const message = 'There was an error deleting the filter';
                showErrorMessage(this, message, e);
            }
        },
        confirmName() {
            const name = (this.tempName || '').trim();
            if (!name) {
                this.cancelName();
                return;
            }
            if (this.pendingAction === 'create') {
                const model = this.collectCurrentState();
                const userSavedGridSearchId = Date.now();
                const entry = {
                    userSavedGridSearchId,
                    name,
                    model,
                    createdAt: new Date().toISOString(),
                    updatedAt: null,
                    shouldApplyByDefault: false
                };
                this.filters.push(entry);
                this.selectedId = userSavedGridSearchId;
                this.saveToStorage();
            } else if (this.pendingAction === 'rename' && this.currentFilter) {
                this.currentFilter.name = name;
                this.updateFilter();
            }
            this.cancelName();
        },
        cancelName() {
            this.pendingAction = null;
            this.tempName = '';
            this.showNameInput = false;
        },

        // default handling
        async toggleDefault() {
            if (!this.currentFilter) 
                return;

            const newDefaultState = !this.currentFilter.shouldApplyByDefault;
            this.currentFilter.shouldApplyByDefault = newDefaultState;

            const api = `${this.gridUrl}/${this.currentFilter.userSavedGridSearchId}/default/${newDefaultState}`;
            const payload = {
                method: 'put'
            };

            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: `Successfully ${newDefaultState ? 'set' : 'unset'} the default filter.`,
                    type: 'success'
                });

                const currentDefaultFilter = this.filters.find(
                    (f) =>
                        f.shouldApplyByDefault && f.userSavedGridSearchId !== this.currentFilter.userSavedGridSearchId
                );

                if (currentDefaultFilter && currentDefaultFilter !== this.currentFilter) {
                    currentDefaultFilter.shouldApplyByDefault = false;
                }
            } catch (e) {
                const message = `There was an error ${newDefaultState ? 'setting' : 'unsetting'} the default filter`;
                showErrorMessage(this, message, e);
            }
        }
    }
};
</script>

<style scoped>
.order-filter-manager {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
}
.label {
    font-size: 12px;
    color: #666;
}
.select {
    min-width: 200px;
    padding: 4px 6px;
    border-radius: 6px;
    border-color: #aaa;
}
.btn {
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.btn:hover {
    background: #f5f5f5;
}
.btn.primary {
    background: #2b93ff;
    color: #fff;
    border-color: #2b93ff;
}
.btn.danger {
    color: #c00;
    border-color: #caa;
}
.name-row {
    display: flex;
    gap: 6px;
    align-items: center;
}
.name-input {
    padding: 6px 8px;
    min-width: 240px;
    border: 1px solid #ccc;
    border-radius: 4px;
}
</style>
